import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {
  BaseOccUrlProperties,
  BaseSiteService,
  DynamicAttributes,
  OccConfig,
  OccEndpointsService,
} from '@spartacus/core';
import {isPlatformBrowser} from '@angular/common';
import {UntilDestroy} from '@ngneat/until-destroy';
import {ConfigService} from './config.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpOccEndpointsService extends OccEndpointsService {
  private isIasActive: boolean = false;

  constructor(
    config: OccConfig,
    baseSiteService: BaseSiteService,
    @Inject(PLATFORM_ID) public platformId: string,
    private configService: ConfigService,
  ) {
    super(config, baseSiteService);
    // check if we are in token exchange mode and need to use UDEX gateway for API calls.
    if (isPlatformBrowser(this.platformId)) {
      this.isIasActive = window.sessionStorage.getItem('iasActiveFlag') === 'true';
    }
  }

  /**
   * Returns a fully qualified OCC Url
   *
   * @param endpoint Name of the OCC endpoint key
   * @param attributes Dynamic attributes used to build the url
   * @param propertiesToOmit Specify properties to not add to the url (baseUrl, prefix, baseSite)
   */
  override buildUrl(endpoint: string, attributes?: DynamicAttributes, propertiesToOmit?: BaseOccUrlProperties): string {
    const originalUrl = super.buildUrl(endpoint, attributes, propertiesToOmit);
    return this.adjustOriginalUrl(endpoint, originalUrl);
  }

  private adjustOriginalUrl(endpoint: string, originalUrl: string) {
    if (this.configService.getDecoratedEndpoint(endpoint)) {
      const decoratorUrl = this.isIasActive
        ? this.configService.getDecoratedEndpoint(endpoint).baseUrlUdex
        : this.configService.getDecoratedEndpoint(endpoint).baseUrl;
      const originalBaseUrl = this.getOriginalBaseUrl(originalUrl);

      return originalUrl.replace(originalBaseUrl, decoratorUrl);
    } else {
      const originalBaseUrl = this.getOriginalBaseUrl(originalUrl);
      originalUrl =
        this.isIasActive && !originalUrl.includes(this.configService.getString('backend.occ.baseUrlUdex'))
          ? originalUrl.replace(originalBaseUrl, this.configService.getString('backend.occ.baseUrlUdex'))
          : originalUrl;
    }
    return originalUrl;
  }

  getOriginalBaseUrl(originalFullUrl: string): string {
    const baseUrlSuffix = this.configService.getString('backend.occ.baseUrlSuffix');
    if (!baseUrlSuffix) throw new Error('baseUrlSuffix should be defined in config backend.occ.baseUrlSuffix');
    const baseUrlSuffixIndex = originalFullUrl.indexOf(baseUrlSuffix, 'https://'.length);
    return originalFullUrl.substring(0, baseUrlSuffixIndex);
  }

  override getBaseUrl(baseUrlProperties?: BaseOccUrlProperties): string {
    let baseUrl = super.getBaseUrl(baseUrlProperties);
    const originalBaseUrl = this.getOriginalBaseUrl(baseUrl);
    baseUrl = this.checkUdexUrl(baseUrl, originalBaseUrl);
    return baseUrl;
  }

  private checkUdexUrl(baseUrl: string, originalBaseUrl: string) {
    const udexUrl = this.configService.getString('backend.occ.baseUrlUdex');
    if (this.isIasActive && baseUrl !== udexUrl) {
      baseUrl = baseUrl.replace(originalBaseUrl, udexUrl);
    }
    return baseUrl;
  }
}
